import { Message } from '@/widget/domain/message';
import { Conversation } from '@/widget/domain/conversation';
import { ChatWidgetStore } from '@/widget/services/useChatWidgetStore';
import { snakeCaseKeys } from '@/lib/objects';
import { storeSet, storeGet } from '@/services/localStorageAdapter';

const requestData = ({ subscription, conversationKey }: {
  subscription: ActionCable.Channel,
  conversationKey: string,
}): boolean => (
  subscription.send({
    action_name: 'find_conversation_messages',
    payload: { key: conversationKey },
  })
);

function sendMessage({
  conversationKey, message, contactInfo, store, subscription,
}: {
  conversationKey: string,
  contactInfo: object | null,
  message: Message,
  store: ChatWidgetStore,
  subscription: ActionCable.Channel,
}): boolean {
  const { messages, setMessages } = store.getState();
  setMessages([...messages || [], message]);

  return subscription.send({
    action_name: 'create_message',
    payload: snakeCaseKeys({
      conversationKey,
      message,
      contactInfo,
    }),
  });
}

function handleMessagesFound({ data, store }: { data: Message[], store: ChatWidgetStore }): void {
  const { setMessages } = store.getState();
  setMessages(data);
}

function messageReceived({ data, store }: { data: Conversation, store: ChatWidgetStore }): void {
  const {
    conversations: conversationsList,
    conversationsLoaded,
    conversationKeys,
    setConversationKeys,
    setConversations,
  } = store.getState();

  if (conversationsLoaded) {
    // Update conversations store to display actual list and save actual keys to localStorage:
    const conversations = [data, ...conversationsList.filter((c) => c.key !== data.key)];
    setConversations(conversations, true);
    storeSet(`slc_${window.ChatWidget.token}_dk`, conversations.map((c) => c.key));
  } else {
    // Just save actual keys to localStorage and update conversationsKeys in state to load actual list later:
    const keys = storeGet(`slc_${window.ChatWidget.token}_dk`) || [];
    setConversationKeys([...conversationKeys, data.key]);
    storeSet(`slc_${window.ChatWidget.token}_dk`, [...keys, data.key]);
  }
}

// Handle new message from operator:
function receiveMessage({ data, store }: { data: Conversation, store: ChatWidgetStore }) {
  messageReceived({ data, store });

  const { activeConversationKey, messages, setMessages } = store.getState();
  if (data.key === activeConversationKey) {
    setMessages([...messages, data.lastMessage]);
  }
  const event = new CustomEvent('s2:widget:message:new', { detail: { key: data.key } });
  dispatchEvent(event);
}

const MessagesDispatcher = {
  requestData,
  handleMessagesFound,
  sendMessage,
  messageReceived,
  receiveMessage,
};

export default MessagesDispatcher;
